/**
 * FormFragmentManager — orchestrates form fragment initialization.
 * Vanilla DOM replacement for the jQuery-based c-form-class.js.
 * Internal class renamed Form → FormFragmentManager to avoid conflict with Form.ts.
 */
import { AbstractFormFragment } from './AbstractFormFragment';
import { CheckBoxGroupFormFragment } from '@molecules/form-checkboxgroup/CheckBoxGroupFormFragment';
import { RadioGroupFormFragment } from '@molecules/form-radiogroup/RadioGroupFormFragment';
import { CaptchaFormFragment } from '@molecules/form-captcha/CaptchaFormFragment';
import { TextFieldFormFragment } from '@molecules/form-text/TextFieldFormFragment';
import { TextAreaFormFragment } from '@molecules/form-textarea/TextAreaFormFragment';
import { DropdownFormFragment } from '@molecules/form-dropdown/DropdownFormFragment';
import { FileUploadFormFragment } from '@molecules/form-fileupload/FileUploadFormFragment';

type FragmentClass = (new (el: HTMLElement) => AbstractFormFragment) & { getCssClassName(): string };

const FORM_FRAGMENT_TYPES: FragmentClass[] = [
  CaptchaFormFragment,
  CheckBoxGroupFormFragment,
  DropdownFormFragment,
  FileUploadFormFragment,
  RadioGroupFormFragment,
  TextFieldFormFragment,
  TextAreaFormFragment,
];

export class FormFragmentManager {
  private ctx: HTMLElement;
  formFragments: AbstractFormFragment[] = [];

  constructor(el: HTMLElement) {
    this.ctx = el;
  }

  init(): this {
    this.formFragments = this._initializeFragments();
    FormFragmentManager._scrollFirstErrorChildIntoView();
    return this;
  }

  static _scrollFirstErrorChildIntoView(): void {
    const firstError = document.querySelector('.comp-form form .error');
    const section = firstError?.closest<HTMLElement>('.section');
    section?.scrollIntoView(false);
  }

  _initializeFragments(): AbstractFormFragment[] {
    const fragments: AbstractFormFragment[] = [];
    for (const FragmentType of FORM_FRAGMENT_TYPES) {
      this.ctx.querySelectorAll<HTMLElement>('.' + FragmentType.getCssClassName()).forEach((el) => {
        fragments.push(new FragmentType(el).initializeNode());
      });
    }
    return fragments;
  }
}

export default FormFragmentManager;
